import { useState, useCallback } from 'react';
import { handleAddModifyOperation } from '../../Api/ApiMethods';

/**
 * Modal submit lifecycle yönetimi hook'u.
 *
 * Sorumlulukları:
 * - isSubmitting state yönetimi
 * - validate() çağrısı → hata varsa early return
 * - buildPayload(formData) ile API payload oluşturma
 * - handleAddModifyOperation çağrısı
 * - Başarıda handleCancel() veya onSuccess() çağrısı
 * - try/catch/finally ile hata yönetimi
 *
 * @param {Object} config
 * @param {Function}        config.validate         — useFormValidation'dan dönen validate()
 * @param {Function}        config.buildPayload     — (formData) => API payload objesi
 * @param {string}          config.apiEndpoint      — API base URL
 * @param {string}          config.mode             — 'add' | 'modify'
 * @param {Object|null}     config.selectedItem     — Düzenlenen öğe (modify modda)
 * @param {Function}        config.refreshData      — Üst bileşenin data refresh callback'i
 * @param {Function}        config.handleCancel     — Modal kapanış callback'i
 * @param {string}          config.componentName    — Toast mesajı için bileşen adı (örn: 'Banka')
 * @param {Object}          config.formData         — Güncel form state'i
 * @param {string}          [config.httpMethod]     — Özel HTTP metodu ('PATCH' vb.)
 * @param {boolean}         [config.isFormData]     — FormData olarak gönderim
 * @param {Function}        [config.onSuccess]      — Başarıda handleCancel yerine çağrılacak callback
 *
 * @returns {{ handleSubmit: Function, isSubmitting: boolean }}
 */
export const useModalSubmit = ({
    validate,
    buildPayload,
    apiEndpoint,
    mode,
    selectedItem,
    refreshData,
    handleCancel,
    componentName,
    formData,
    httpMethod,
    isFormData: isFormDataFlag,
    onSuccess,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = useCallback(async (e) => {
        // Form submit event ise default'u engelle
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        // Validation
        const errors = validate();
        if (Object.keys(errors).length > 0) return;

        setIsSubmitting(true);

        try {
            const payload = buildPayload ? buildPayload(formData) : { ...formData };

            const result = await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? selectedItem : null,
                mode,
                data: payload,
                refreshDataCallback: refreshData,
                componentName,
                ...(httpMethod && { httpMethod }),
                ...(isFormDataFlag && { isFormData: true }),
            });

            if (onSuccess) {
                onSuccess(result);
            } else {
                handleCancel();
            }
        } catch {
            // Hata toast'ı handleAddModifyOperation tarafından basılır
        } finally {
            setIsSubmitting(false);
        }
    }, [
        validate, buildPayload, apiEndpoint, mode, selectedItem,
        refreshData, handleCancel, componentName, formData,
        httpMethod, isFormDataFlag, onSuccess,
    ]);

    return { handleSubmit, isSubmitting };
};
